<template>
  <loader :is-loading="isPending && isDealContext" />
  <div
    v-on-click-outside="handleClickOutside"
    data-cy="contacts-modal"
    class="z-1 bg-grey-hover"
    :class="[
      !ignorePlacement && {
        'top-7 left-28': position === 'bottom',
        'bottom-12 left-0': position === 'top',
      },
      { absolute: !ignorePlacement },
    ]"
  >
    <contacts-list
      v-if="contacts"
      :id="value.id"
      :deal="isDealContext ? value : null"
      :contacts="contacts"
      class="max-h-70 min-h-40 w-[450px] overflow-y-auto bg-grey-hover p-2"
    />
  </div>
</template>
<script lang="ts" setup>
import { watchImmediate } from "@vueuse/core";
import { computed, onBeforeUnmount, ref } from "vue";
import { useRoute } from "vue-router";

import { CalleeType } from "@/enums/communicationLogs";
import { getContactsWithCalleeType } from "@/helpers/communication";
import { PHONE_ACTION_MODAL_OPEN } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useContacts } from "@/hooks/contacts";
import { useApplicationPhoneNumbersQuery } from "@/hooks/queries/applications";

import Loader from "@/components/ui/Loader.vue";
import ContactsList from "@/views/deals/components/contacts/ContactsList.vue";

import type {
  ApplicationWithContactData,
  ContactProfile,
} from "@/models/applications";
import type { ContactTelephones } from "@/models/communicationLogs";
import type { PropType } from "vue";

const props = defineProps({
  value: {
    type: Object as PropType<ApplicationWithContactData>,
    required: true,
  },
  position: {
    type: String as PropType<"top" | "bottom">,
    default: "bottom",
  },
  ignorePlacement: {
    type: Boolean,
    default: false,
  },
});

const { contacts, setContacts } = useContacts();
const route = useRoute();
const emitter = useEmitter();

const hasOpenChildModal = ref(false);

const emit = defineEmits<{
  closeModal: [];
}>();

const isDealContext = computed(() => route.fullPath.startsWith("/deals"));

const dealId = computed(() => (isDealContext.value ? props.value.id : ""));

const { data: phoneNumbers, isPending } =
  useApplicationPhoneNumbersQuery(dealId);

const handleContacts = () => {
  if (isDealContext.value) {
    const contactsWithCalleeType = getContactsWithCalleeType({
      business: props.value.business,
      owners: [
        props.value.personal_information,
        ...(props.value.business?.other_owners || []),
      ],
    });
    const contactsInfo = contactsWithCalleeType.map((contact) => {
      if (contact.callee_type === CalleeType.Business) {
        const allTelephones =
          phoneNumbers.value?.business?.all_telephones || [];
        return {
          ...contact,
          telephones: allTelephones.map(({ number }) => number),
          all_telephones: allTelephones,
        };
      }
      const ownerData = phoneNumbers.value?.owners.find(
        ({ id }) => id === contact.id
      );
      return {
        ...contact,
        telephones:
          ownerData?.all_telephones?.map(({ number }) => number) ??
          contact.telephones,
        all_telephones: ownerData?.all_telephones ?? [],
      };
    });
    setContacts(props.value.id, contactsInfo);
    return;
  }
  const contactsInfo = [
    {
      business_legal_name: props.value.name,
      telephones:
        props.value?.phone_numbers?.map((business) => business.phone_number) ||
        [],
    },
    ...(props.value?.relationships?.reduce((acc: ContactProfile[], entity) => {
      if (entity.type === "owner") {
        acc.push(entity.profile);
      }
      return acc;
    }, []) || []),
  ] as ContactTelephones[];
  setContacts(props.value.id, contactsInfo);
};

emitter.on(PHONE_ACTION_MODAL_OPEN, (isOpen: boolean) => {
  hasOpenChildModal.value = isOpen;
});

const handleClickOutside = () => {
  if (!hasOpenChildModal.value) {
    emit("closeModal");
  }
};

watchImmediate(phoneNumbers, handleContacts);

onBeforeUnmount(() => {
  emitter.off(PHONE_ACTION_MODAL_OPEN);
});
</script>
